import { Envs } from '@traba/types'
import { handleDates, isAxiosError } from '@traba/utils'
import axios, { InternalAxiosRequestConfig } from 'axios'
import { getAuth } from 'firebase/auth'
import Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import { recordFailedRequest } from './failed-request-log'

export const BACKEND_URL = import.meta.env['VITE_BACKEND_URL']

const APP = import.meta.env['VITE_APP']
export let firebaseToken: string | undefined

const devBackendUrl = import.meta.env[
  `VITE_${Envs.DEVELOPMENT.toUpperCase()}_BACKEND_URL`
]
const prodBackendUrl = import.meta.env[
  `VITE_${Envs.PRODUCTION.toUpperCase()}_BACKEND_URL`
]

export const trabaApis = new Map([
  [Envs.DEVELOPMENT, axios.create({ baseURL: devBackendUrl })],
  [Envs.PRODUCTION, axios.create({ baseURL: prodBackendUrl })],
] as const)

export const trabaApi = axios.create({
  baseURL: BACKEND_URL,
})

// OPS console auth token
async function createOpsGoogleTokenConfig(
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
  const nodeServiceToken = Cookies.get('nodeServiceToken')
  const googleTokenCookie = Cookies.get('opsConsoleIdToken')
  const googleToken = localStorage.getItem('opsConsoleIdToken')
  config.headers['Authorization'] = `Bearer ${googleTokenCookie ?? googleToken}`
  config.headers['Accept'] = 'application/json'
  config.headers['Content-Type'] = 'application/json'
  if (nodeServiceToken) {
    config.headers['node-service-token'] = nodeServiceToken
  }
  if (import.meta.env['NODE_ENV'] !== 'production') {
    config.headers['ngrok-skip-browser-warning'] = '69420'
  }
  return config
}

// Biz auth token
async function createFirebaseTokenConfig(
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
  const auth = getAuth()
  firebaseToken = await auth.currentUser?.getIdToken()
  config.headers['Authorization'] = `Bearer ${firebaseToken}`
  config.headers['Accept'] = 'application/json'
  config.headers['Content-Type'] = 'application/json'
  return config
}

for (const value of [...trabaApis.values(), trabaApi]) {
  value.interceptors.request.use((config) => {
    const requestId = uuidv4()
    config.headers['X-Request-ID'] = requestId
    return config
  })

  value.interceptors.request.use(
    APP === 'OPS' ? createOpsGoogleTokenConfig : createFirebaseTokenConfig,
    (error) => Promise.reject(error),
  )
}

async function handleRetry(error: unknown) {
  if (!isAxiosError(error)) {
    return null
  }

  const status = error.response?.status
  const retryCount = error.config?._retry || 0

  if (status && [502, 503].includes(status) && retryCount < 3) {
    console.log(
      `Received ${status}, retrying request`,
      error.config?.url || 'unknown URL',
    )

    if (error.config) {
      error.config._retry = retryCount + 1
      const delay = Math.random() * 1000 * error.config._retry + 1000
      await new Promise((resolve) => setTimeout(resolve, delay))
      return trabaApi(error.config)
    }
  }
  return null
}
// OPS error handling
async function handleErrorOps(error: unknown) {
  if (!isAxiosError(error)) {
    console.error(
      'Error',
      error instanceof Error ? error.message : String(error),
    )
    console.error('Raw Error JSON', JSON.stringify(error))
    throw error
  }

  if (error.response) {
    const retryResult = await handleRetry(error)
    if (retryResult) {
      return retryResult
    }
    recordFailedRequest(error)
    console.error(error.response.data)
    throw error.response.data
  } else if (error.request) {
    if (!axios.isCancel(error)) {
      recordFailedRequest(error)
    }
    console.dir(error.request)
    console.error('Raw Error JSON', JSON.stringify(error))
    throw error
  } else {
    console.error('Error', error.message || 'Unknown error')
    console.error('Raw Error JSON', JSON.stringify(error))
    throw error
  }
}
// Biz error handling
async function handleErrorBiz(error: unknown) {
  if (!isAxiosError(error)) {
    console.error(
      'Error',
      error instanceof Error ? error.message : String(error),
    )
    console.error('Raw Error JSON', JSON.stringify(error))
    throw error
  }

  if (error.response) {
    if (
      error.response.status === 401 &&
      error.config &&
      !error.config._retry_auth
    ) {
      const auth = getAuth()
      const newToken = await auth.currentUser?.getIdToken(true)

      // Update the auth token in the original request headers
      if (error.config.headers) {
        error.config.headers['Authorization'] = `Bearer ${newToken}`
      }
      error.config._retry_auth = true

      return trabaApi(error.config)
    } else {
      const retryResult = await handleRetry(error)
      if (retryResult) {
        return retryResult
      }
    }
    console.error(error.response.data)
    throw error.response.data
  } else if (error.request) {
    console.dir(error.request)
    console.error('Raw Error JSON', JSON.stringify(error))
    throw error
  } else {
    console.error('Error', error.message || 'Unknown error')
    console.error('Raw Error JSON', JSON.stringify(error))
    throw error
  }
}

trabaApi.interceptors.response.use(
  (originalResponse) => {
    handleDates(originalResponse.data)
    return originalResponse
  },
  async (error) => {
    if (APP === 'OPS') {
      return handleErrorOps(error)
    } else {
      return handleErrorBiz(error)
    }
  },
)
